import {
  Card,
  CardHeader,
  CardBody,
  Label,
  FormGroup,
  Input,
  Container,
  Row,
  Col,
} from "reactstrap";
import Select from "react-select";
import React, { useState, useEffect, useContext, useMemo } from "react";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import AdminHeader from "components/Headers/AdminHeader";
import {
  addTrainingCenter,
  editTrainingCenter,
  getStates,
  getCities,
  getTrainingCenterByID,
} from "action/trainingCenter";
import { SELECT_CUSTOM_STYLES } from "constants/CommonConstants";
import { NotificationContext } from "components/CommonComponents/NotificationProvider";
import { useDispatch, useSelector } from "react-redux";
import { REGEX } from "constants/CommonConstants";
import { urlDecodedId } from "components/CommonComponents/encodeDecodeFunction";
import CreatableSelect from "components/CommonComponents/CreatableSelect";
import AddNewCityModal from "components/CommonComponents/AddNewCityModal";
import { addCity } from "action/channelPartner";

const initialFields = {
  name: {
    value: "",
    error: "",
    isChanged: false,
  },
  addressLineOne: {
    value: "",
    error: "",
    isChanged: false,
  },
  addressLineTwo: {
    value: "",
    error: "",
    isChanged: false,
  },
  cityID: {
    value: "",
    error: "",
    isChanged: false,
  },
  stateID: {
    value: "",
    error: "",
    isChanged: false,
  },
  pincode: {
    value: "",
    error: "",
    isChanged: false,
  },
  status: {
    value: "ACTIVE",
    error: "",
    isChanged: false,
  },
};

function AddTrainingCenter() {
  const authData = JSON.parse(localStorage.getItem("p") || "{}");
  const role = authData?.role;

  const { id } = useParams();
  const { notify } = useContext(NotificationContext);
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const states =
    useSelector((state) => state.trainingCenters.states?.data?.states) || [];
  const cities =
    useSelector((state) => state.trainingCenters.cities?.data?.cities) || [];
  const mode = location.state?.mode || "add";
  const decodedId = id ? urlDecodedId(id) : null;
  const [formData, setFormData] = useState(initialFields);
  const [showLoaderOnClick, setShowLoaderOnClick] = useState(false);
  const [showAlternative, setShowAlternative] = useState({
    loader: false,
    error: "",
  });

  const [showLoaderOnCity, setShowLoaderOnCity] = useState(false);
  const [newCityName, setNewCityName] = useState("");
  const [showAddCityModal, setShowAddCityModal] = useState({
    isopen: false,
    stateName: null,
    stateID: null,
  });

  // Prefill form when in edit mode using static data
  useEffect(() => {
    if (mode === "edit") {
      if (!decodedId) {
        notify("danger", "Invalid training center");
        navigate(
          role === "SUPER_ADMIN"
            ? "/super-admin/training-center"
            : "/admin/training-center",
        );
        return;
      }
      getTrainingCenter(decodedId);
    }
  }, [mode, decodedId]);

  const getTrainingCenter = async (id) => {
    try {
      setShowAlternative({ loader: true, error: "" });
      const res = await getTrainingCenterByID(id);

      if (res?.code === 200) {
        const trainingData = res?.data;

        setFormData((prev) => ({
          ...prev,
          name: { value: trainingData?.name || "", error: "" },

          addressLineOne: {
            value: trainingData?.addressLineOne || "",
            error: "",
          },
          addressLineTwo: {
            value: trainingData?.addressLineTwo || "",
            error: "",
          },
          pincode: {
            value: trainingData?.pincode || "",
            error: "",
          },
          stateID: {
            value: trainingData?.cityResponse?.state?.ID || "",
            error: "",
          },
          cityID: {
            value: trainingData?.cityResponse?.ID || "",
            error: "",
          },
          status: {
            value: trainingData?.status || "ACTIVE",
            error: "",
          },
        }));

        // Fetch cities for selected state
        if (trainingData?.cityResponse?.state?.ID) {
          fetchCitiesForState(trainingData.cityResponse.state.ID);
        }
        setShowAlternative({ loader: false});
      } else {
        setShowAlternative({ loader: false});
        const message = Array.isArray(res?.message)
          ? res.message
              .map((err) => (err.Msg ? err.Msg : JSON.stringify(err)))
              .join(", ")
          : res?.message || "Failed to load training center details!";
        notify("danger", message);
      }
    } catch (err) {
      setShowAlternative({ loader: false});
    } finally {
      setShowAlternative({ loader: false});
    }
  };

  useEffect(() => {
    getStates(dispatch);
  }, [dispatch]);

  const stateOptions = useMemo(() => {
    return states.map((r) => ({
      value: r.ID,
      label: r.name,
    }));
  }, [states]);

  const cityOptions = useMemo(() => {
    return cities.map((c) => ({
      value: c.ID,
      label: c.name,
    }));
  }, [cities]);

  const fetchCitiesForState = (stateID) => {
    const cityFilter = JSON.stringify([
      {
        field: "StateID",
        condition: "eq",
        filterValues: [String(stateID)],
      },
    ]);

    getCities(dispatch, cityFilter);
  };

  const handleInputChange = (field, value) => {
    setFormData((prev) => {
      const updatedData = {
        ...prev,
        [field]: {
          ...prev[field],
          value,
          error: "",
          isChanged: true,
        },
      };

      if (field === "stateID") {
        updatedData.cityID = {
          ...prev.cityID,
          value: "",
          error: "",
          isChanged: true,
        };
      }

      return updatedData;
    });
  };

  const getValidationError = (name, value, allValues = {}) => {
    // const isEmpty =
    //   value === "" ||
    //   value === null ||
    //   (Array.isArray(value) && value.length === 0);

    switch (name) {
      case "name":
        if (!value) return "Please enter center name";
        if (value.trim().length < 5)
          return "Training center name must be at least 5 characters";
        if (value.trim().length > 150)
          return "Training center name cannot exceed 150 characters";
        // if (!REGEX.ALPHANUMERIC_SPACE_REGEX.test(value)) {
        //   return "Training center can contain only letters and numbers";
        // }

        return "";

      case "addressLineOne":
        if (!value) return "Please enter address line 1";

        if (value.trim().length < 5)
          return "Address line must be at least 5 characters";

        if (value.trim().length > 150)
          return "Address line cannot exceed 150 characters";

        return "";
      case "addressLineTwo":
        if (value.trim().length > 150)
          return "Address line cannot exceed 150 characters";

        return "";

      case "cityID":
        return value ? "" : "Please select city";

      case "stateID":
        return value ? "" : "Please select state";

      case "pincode":
        if (!value) return "Please enter pincode";
        if (!REGEX.PIN_CODE_REGEX.test(value))
          return "Pincode must be 6 digits";
        return "";

      default:
        return "";
    }
  };

  const handleOnBlur = (name, value) => {
    const trimmed = typeof value === "string" ? value.trim() : value;
    const error = getValidationError(name, trimmed);
    setFormData((prev) => ({
      ...prev,
      [name]: {
        ...prev[name],
        value: trimmed,
        error,
      },
    }));
  };

  const handleChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: {
        ...prev[field],
        value,
        error: "",
        isChanged: true,
      },
    }));
  };

  const validateForm = () => {
    let newUser = { ...formData };

    Object.keys(newUser).forEach((key) => {
      newUser[key] = {
        ...newUser[key],
        error: getValidationError(key, newUser[key].value),
      };
    });

    setFormData(newUser); // set the error state

    return Object.values(newUser).every((item) => item.error === "");
  };

  //while edit get only edit data
  const getEditedBodyData = (formData) =>
    Object.fromEntries(
      Object.entries(formData) //loop over each child
        .filter(([, field]) => field.isChanged) //remove or filter the unchanges fields
        .map(([key, field]) => {
          return [key, field.value];
        }),
    );

  const handleSave = async () => {
    if (!validateForm()) return;

    try {
      setShowLoaderOnClick(true);

      const bodyData =
        mode === "edit"
          ? getEditedBodyData(formData)
          : {
              countryID: 1, // static for now
              stateID: formData.stateID.value,
              cityID: formData.cityID.value,
              name: formData.name.value,
              addressLineOne: formData.addressLineOne.value,
              addressLineTwo: formData.addressLineTwo.value,
              pincode: formData.pincode.value,
              status: formData.status.value,
            };

      if (mode === "add") {
        const res = await addTrainingCenter(bodyData);

        if (res?.code === 200) {
          notify(
            "success",
            res.message || "Training Center added successfully",
          );
          // navigate back to list after successful message display
          navigate(
            role === "SUPER_ADMIN"
              ? "/super-admin/training-center"
              : "/admin/training-center",
          );
        } else {
          const message = Array.isArray(res?.message)
            ? res.message
                .map((err) => (err?.Msg ? err.Msg : JSON.stringify(err)))
                .join(", ")
            : res?.message || "Failed to add training center";
          notify("danger", message);
        }
      } else {
        if (Object.keys(bodyData).length === 0) {
          notify("info", "No changes detected");
          return;
        }

        const res = await editTrainingCenter(decodedId, bodyData);

        if (res?.code === 200) {
          notify(
            "success",
            res.message || "Training Center updated successfully",
          );

          navigate(
            role === "SUPER_ADMIN"
              ? "/super-admin/training-center"
              : "/admin/training-center",
          ); // navigate back to list after successful message display
        } else {
          const message = Array.isArray(res?.message)
            ? res?.message[0]?.Msg || JSON.stringify(res?.message)
            : res?.message || "Failed to update training center!";
          notify("danger", message);
        }
      }
    } catch (err) {
      notify("danger", err?.message || "Something went wrong");
    } finally {
      setShowLoaderOnClick(false);
    }
  };

  const handleSaveNewCity = async (stateID) => {
    setShowLoaderOnCity(true);
    try {
      const res = await addCity({
        stateID: stateID,
        name: newCityName,
        status: "ACTIVE",
      });
      if (res?.code === 200) {
        notify("success", res?.message || "City added successfully");
        fetchCitiesForState(stateID);
        setShowAddCityModal({ isopen: false, stateID: null, stateName: null });
        setNewCityName("");
      } else {
        const message = Array.isArray(res?.message)
          ? res.message
              .map((err) => (err.Msg ? err.Msg : JSON.stringify(err)))
              .join(", ")
          : res?.message || "Failed to add city";
        notify("danger", message);
      }
    } catch (error) {
      notify("danger", error?.message || "Failed to add city");
    } finally {
      setShowLoaderOnCity(false);
    }
  };

  const handleOpenCityModal = () => {
    setShowAddCityModal({
      isopen: true,
      stateID: formData.stateID.value,
      stateName: stateOptions.find((s) => s.value === formData.stateID.value)
        ?.label,
    });
  };

  const handleCancelAddCity = () => {
    setShowAddCityModal({ isopen: false, stateID: null, stateName: null });
    setNewCityName("");
  };

  return (
    <>
      <AdminHeader parentName="Training Centers" />
      <Container className="mt--6" fluid>
        <Card className="mb-4">
          <CardHeader className="d-flex align-items-center ">
            <i
              className="fa fa-chevron-left mr-2 cursor"
              onClick={() =>
                navigate(
                  role === "SUPER_ADMIN"
                    ? "/super-admin/training-center"
                    : "/admin/training-center",
                )
              }
            />
            <h3 className="mb-0 text-default">
              {mode === "edit" ? "Edit Training Center" : "Add Training Center"}
            </h3>
          </CardHeader>

          <CardBody>
            {mode === "edit" && showAlternative.loader ? (
              <div className="loaderContainer">
                <i className="fa fa-spinner fa-spin fa-2xl loader" />
              </div>
            ) : (
              <>
                <Row>
                  <Col md="4">
                    <FormGroup>
                      <Label className="form-control-label">
                        Center Name <span className="text-danger"> *</span>
                      </Label>
                      <Input
                        type="text"
                        name="name"
                        value={formData.name.value}
                        placeholder="Enter center name"
                        onChange={(e) =>
                          handleInputChange("name", e.target.value)
                        }
                        onBlur={(e) =>
                          handleOnBlur(e.target.name, e.target.value)
                        }
                        className={formData.name.error ? "is-invalid" : ""}
                        minLength={5}
                        maxLength={150}
                      />
                      {formData.name.error && (
                        <small className="invalid-feedback d-block">
                          {formData.name.error}
                        </small>
                      )}
                    </FormGroup>
                  </Col>

                  <Col md="4">
                    <FormGroup>
                      <Label className="form-control-label">
                        Address Line 1 <span className="text-danger"> *</span>
                      </Label>
                      <Input
                        type="text"
                        name="addressLineOne"
                        value={formData.addressLineOne.value}
                        placeholder="Enter address line 1"
                        onChange={(e) =>
                          handleInputChange("addressLineOne", e.target.value)
                        }
                        onBlur={(e) =>
                          handleOnBlur(e.target.name, e.target.value)
                        }
                        className={
                          formData.addressLineOne.error ? "is-invalid" : ""
                        }
                        minLength={5}
                        maxLength={150}
                      />

                      {formData.addressLineOne.error && (
                        <small className="invalid-feedback d-block">
                          {formData.addressLineOne.error}
                        </small>
                      )}
                    </FormGroup>
                  </Col>

                  <Col md="4">
                    <FormGroup>
                      <Label className="form-control-label">
                        Address Line 2
                      </Label>
                      <Input
                        type="text"
                        name="addressLineTwo"
                        value={formData.addressLineTwo.value}
                        placeholder="Enter address line 2"
                        onChange={(e) =>
                          handleInputChange("addressLineTwo", e.target.value)
                        }
                        onBlur={(e) =>
                          handleOnBlur(e.target.name, e.target.value)
                        }
                        className={
                          formData.addressLineTwo.error ? "is-invalid" : ""
                        }
                        maxLength={150}
                      />
                    </FormGroup>
                  </Col>
                </Row>

                <Row>
                  <Col md="4">
                    <FormGroup>
                      <Label className="form-control-label">
                        State <span className="text-danger"> *</span>
                      </Label>

                      <Select
                        options={stateOptions}
                        placeholder="Select state"
                        classNamePrefix="react-select"
                        value={stateOptions.find(
                          (opt) => opt.value === formData.stateID.value,
                        )}
                        onChange={(selected) => {
                          handleInputChange("stateID", selected.value);
                          fetchCitiesForState(selected.value);
                        }}
                        styles={SELECT_CUSTOM_STYLES}
                        menuPortalTarget={document.body}
                        className={formData.stateID.error ? "is-invalid" : ""}
                      />
                      {formData.stateID.error && (
                        <small className="invalid-feedback d-block">
                          {formData.stateID.error}
                        </small>
                      )}
                    </FormGroup>
                  </Col>

                  <Col md="4">
                    <FormGroup>
                      <Label className="form-control-label">
                        City <span className="text-danger"> *</span>
                      </Label>
                      <CreatableSelect
                        placeholder="Select city"
                        options={cityOptions}
                        value={formData.cityID.value}
                        onChange={(selected) =>
                          handleInputChange("cityID", selected.value)
                        }
                        isDisabled={!formData.stateID.value}
                        onAddNew={() => handleOpenCityModal()}
                        className={formData.cityID.error ? "is-invalid" : ""}
                      />
                      {formData.cityID.error && (
                        <small className="invalid-feedback d-block">
                          {formData.cityID.error}
                        </small>
                      )}
                    </FormGroup>
                  </Col>

                  <Col md="4">
                    <FormGroup>
                      <Label className="form-control-label">
                        Pincode <span className="text-danger">*</span>
                      </Label>
                      <Input
                        type="text"
                        name="pincode"
                        value={formData.pincode.value}
                        placeholder="Enter pincode"
                        maxLength={6}
                        onChange={(e) =>
                          handleInputChange(
                            "pincode",
                            e.target.value.replace(/\D/g, ""),
                          )
                        }
                        onBlur={(e) =>
                          handleOnBlur(e.target.name, e.target.value)
                        }
                        className={formData.pincode.error ? "is-invalid" : ""}
                      />
                      {formData.pincode.error && (
                        <small className="invalid-feedback d-block">
                          {formData.pincode.error}
                        </small>
                      )}
                    </FormGroup>
                  </Col>

                  <Col md="4">
                    <FormGroup>
                      <Label className="form-control-label">
                        Status <span className="text-danger"> *</span>
                      </Label>

                      <div className="d-flex mt-2">
                        <FormGroup check className="mr-4">
                          <Label check>
                            <Input
                              type="radio"
                              name="status"
                              checked={
                                formData.status.value === "ACTIVE"
                              }
                              onChange={() =>
                                handleChange("status", "ACTIVE")
                              }
                            />
                            Active
                          </Label>
                        </FormGroup>

                        <FormGroup check>
                          <Label check>
                            <Input
                              type="radio"
                              name="status"
                              checked={
                                formData.status.value === "INACTIVE"
                              }
                              onChange={() =>
                                handleChange("status", "INACTIVE")
                              }
                            />
                            Inactive
                          </Label>
                        </FormGroup>
                      </div>
                    </FormGroup>
                  </Col>
                </Row>
              </>
            )}

            {/* Action Buttons */}
            <Row className="mt-4">
              <Col className="text-right">
                <button
                  className="btn btn-secondary mr-2 save-btn-sm"
                  onClick={() =>
                    navigate(
                      role === "SUPER_ADMIN"
                        ? "/super-admin/training-center"
                        : "/admin/training-center",
                    )
                  }
                >
                  Cancel
                </button>

                <button
                  className="btn btn-deep-blue save-btn-sm"
                  type="submit"
                  onClick={handleSave}
                  disabled={showLoaderOnClick}
                >
                  {showLoaderOnClick ? (
                    <i className="fa fa-spinner fa-spin fa-lg text-white" />
                  ) : (
                    "Save"
                  )}
                </button>
              </Col>
            </Row>
          </CardBody>
        </Card>
      </Container>
      {showAddCityModal && (
        <AddNewCityModal
          isOpen={showAddCityModal.isopen}
          stateID={showAddCityModal.stateID}
          stateName={showAddCityModal.stateName}
          cityName={newCityName}
          setCityName={setNewCityName}
          onSave={handleSaveNewCity}
          onCancel={handleCancelAddCity}
          showLoader={showLoaderOnCity}
        />
      )}
    </>
  );
}

export default AddTrainingCenter;
